import { useId, type ReactNode } from "react";
import { useTranslation } from "../../../i18n/useTranslation";
import { useNow } from "../../../lib/useNow";
import type { UnavailableReason } from "../../../features/guestCheckout/machine";
import { HelpLink, Line, OfflineNote, Screen, useAt } from "./GuestScreen";

export type { HoldView } from "./GuestScreen";

/**
 * The guest screens that stay in the entry. The code step and change email
 * are the `guest-code` chunk (GuestCodeSteps.tsx); the parts all of them
 * share are in GuestScreen.tsx.
 */

/**
 * The last tap could not read the anonymous session, so it sent nothing.
 * Shown wherever that can happen before anything was dispatched.
 */
export function SessionNote() {
  const { t } = useTranslation();
  return (
    <Line tone="warning" role="alert" testId="guest-session-note">
      {t("guest.code.sessionUnavailable")}
    </Line>
  );
}

/* ---------- the exits ---------- */

export function HoldEnded({
  onRecheck,
  onAnother,
}: {
  when: string;
  name: string;
  email: string;
  onRecheck: () => void;
  onAnother: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Screen
      testId="guest-hold-ended"
      heading={t("guest.holdEnded.heading")}
      sub={t("guest.holdEnded.sub")}
      foot={
        <>
          <button type="button" className="b-btn v-primary is-full" onClick={onRecheck} data-testid="guest-recheck">
            {t("guest.holdEnded.recheck")}
          </button>
        </>
      }
      secondary={
        <>
          <button type="button" className="b-btn v-secondary is-full" onClick={onAnother} data-testid="guest-another">
            {t("guest.holdEnded.another")}
          </button>
        </>
      }
    >

    </Screen>
  );
}

/**
 * NO BOOKING WAS MADE, AND THE PERSON IS NOT STUCK HERE (rc.13; the Founder's
 * dead-end finding on Dev). Every refusal that lands here is definite and
 * wrote nothing. The primary action is the one that can actually succeed for
 * THIS reason (the Web Director, after the rc.13 review's wording note):
 *
 *  - `delivery-unavailable` (the address cannot receive the code): "Change
 *    email", back to Details where the address is edited. "Choose another
 *    time" stays, secondary: a new time does not fix the address.
 *  - `verification-off` (guest booking is switched off): "Sign in", because a
 *    signed-in booking still works, when sign-in is offered here (`onSignIn`:
 *    not while a booking is unresolved). Otherwise only "Get help". "Choose
 *    another time" is not offered: it would land on the same refusal.
 *  - `guest-booking-unavailable`: "Choose another time" (the same way out as
 *    the hold-ended screen: the hold is dropped, then the time step).
 *
 * "Get help" stays on every one.
 */
export function GuestUnavailable({
  business,
  reason,
  onAnother,
  onChangeEmail,
  onSignIn,
}: {
  business: string;
  reason: UnavailableReason;
  onAnother: () => void;
  onChangeEmail: () => void;
  /** Absent when sign-in is not offered here. */
  onSignIn?: () => void;
}) {
  const { t } = useTranslation();
  const another = (variant: "v-primary" | "v-secondary") => (
    <button type="button" className={`b-btn ${variant} is-full`} onClick={onAnother} data-testid="guest-unavailable-another">
      {t("guest.holdEnded.another")}
    </button>
  );
  const help = <HelpLink context={{ heading: t("guest.unavailable.heading"), business }} />;
  let foot: ReactNode = null;
  let secondary: ReactNode = help;
  if (reason === "delivery-unavailable") {
    foot = (
      <button type="button" className="b-btn v-primary is-full" onClick={onChangeEmail} data-testid="guest-unavailable-change-email">
        {t("guest.code.changeEmail")}
      </button>
    );
    secondary = (
      <>
        {another("v-secondary")}
        {help}
      </>
    );
  } else if (reason === "verification-off") {
    foot = onSignIn ? (
      <button type="button" className="b-btn v-primary is-full" onClick={onSignIn} data-testid="guest-unavailable-signin">
        {t("flow.signin.submit")}
      </button>
    ) : null;
  } else {
    foot = another("v-primary");
  }
  return (
    <Screen testId="guest-unavailable" heading={t("guest.unavailable.heading")} foot={foot} secondary={secondary}>
      {/* FAIL CLOSED. There is deliberately no route from here to the old
          unverified checkout: that path is what 1.11 retires. */}
      {t("guest.unavailable.body") ? <p className="b-p">{t("guest.unavailable.body", { business })}</p> : null}
    </Screen>
  );
}

export function RequestProblem({
  canRetry,
  onRetry,
  sessionUnavailable = false,
}: {
  canRetry: boolean;
  onRetry: () => void;
  sessionUnavailable?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Screen
      testId="guest-request-problem"
      heading={t("guest.requestProblem.heading")}
      foot={
        canRetry ? (
          <button type="button" className="b-btn v-primary is-full" onClick={onRetry} data-testid="guest-request-retry">
            {t("guest.code.sendNew")}
          </button>
        ) : null
      }
    >
      {sessionUnavailable ? <SessionNote /> : null}
      {t("guest.requestProblem.body") ? <p className="b-p">{t("guest.requestProblem.body")}</p> : null}
    </Screen>
  );
}

export function RequestLimited({
  retryAt,
  reason = "rate-limited",
  timeZone,
  onRetry,
  onBack,
  sessionUnavailable = false,
  clock = Date.now,
}: {
  retryAt: number;
  reason?: "too-soon" | "rate-limited";
  timeZone: string;
  onRetry: () => void;
  /**
   * rc.13 (the Founder's dead-end finding): before `retryAt` this screen had
   * no action at all. "Back" returns to Review (a fresh checkout; nothing was
   * sent). The server's limiter stays authoritative for the next request.
   */
  onBack: () => void;
  sessionUnavailable?: boolean;
  clock?: () => number;
}) {
  const { t } = useTranslation();
  const at = useAt(timeZone);
  const now = useNow(retryAt, clock);
  return (
    <Screen
      testId="guest-request-limited"
      heading={t(reason === "too-soon" ? "guest.requestWait.heading" : "guest.requestLimited.heading")}
      foot={
        now >= retryAt ? (
          <button type="button" className="b-btn v-primary is-full" onClick={onRetry} data-testid="guest-request-retry">
            {t("guest.code.sendNew")}
          </button>
        ) : null
      }
      secondary={
        <button type="button" className="b-btn v-secondary is-full" onClick={onBack} data-testid="guest-request-back">
          {t("flow.back")}
        </button>
      }
    >
      {sessionUnavailable ? <SessionNote /> : null}
      <p className="b-p">
        {t(reason === "too-soon" ? "guest.requestWait.body" : "guest.requestLimited.body", { time: at(retryAt) })}
      </p>
    </Screen>
  );
}

export function IdentityLost({ onRestart }: { onRestart: () => void }) {
  const { t } = useTranslation();
  return (
    <Screen
      testId="guest-identity-lost"
      heading={t("guest.identityLost.heading")}
      foot={
        <button type="button" className="b-btn v-primary is-full" onClick={onRestart} data-testid="guest-restart">
          {t("guest.identityLost.action")}
        </button>
      }
    >
      <p className="b-p">{t("guest.identityLost.body")}</p>
    </Screen>
  );
}

/**
 * The lost booking answer. Product's approved copy, unchanged, and one action:
 * ask about THE SAME attempt. When the identity it belongs to is gone, the
 * action is withdrawn and a person is named instead.
 */
export function GuestUncertain({
  business,
  checking,
  checkAlsoLost,
  canCheck,
  sessionUnavailable = false,
  onCheck,
}: {
  /** The public business name, for the outcome title only. */
  business: string;
  checking: boolean;
  checkAlsoLost: boolean;
  canCheck: boolean;
  sessionUnavailable?: boolean;
  onCheck: () => void;
}) {
  const bodyId = useId();
  const { t } = useTranslation();
  return (
    <Screen
      testId="guest-uncertain"
      heading={t("flow.review.uncertain.title")}
      outcome={{ business }}
      describedBy={bodyId}
      // Check is replaced by Get help when the identity goes: focus follows.
      focusKey={canCheck ? "check" : "help"}
      foot={
        canCheck ? (
          <button
            type="button"
            className={`b-btn v-primary is-full${checking ? " is-off" : ""}`}
            aria-disabled={checking || undefined}
            aria-busy={checking || undefined}
            onClick={() => {
              if (!checking) onCheck();
            }}
            data-testid="guest-check"
          >
            {checking ? <span className="b2-spin" aria-hidden="true" /> : null}
            {checking ? t("flow.review.uncertain.busy") : t("flow.review.uncertain.action")}
          </button>
        ) : (
          <HelpLink />
        )
      }
    >
      <OfflineNote />
      {sessionUnavailable && canCheck ? (
        <Line tone="warning" role="alert" testId="guest-session-note">
          {t("guest.uncertain.sessionUnavailable")}
        </Line>
      ) : null}
      {/* POLITE, AS THE LEGACY SCREEN WAS REVIEWED (booking-recovery-1_4:217):
          an unresolved booking is not an error that should interrupt a screen
          reader, and the body is the only text that changes on a second loss.
          Focus lands on the heading (Screen), which carries this text as its
          description, so the explanation is heard on arrival; the region
          itself announces only later changes, and is not the focus target. */}
      <Line tone="warning" role="status" testId="guest-uncertain-body" id={bodyId}>
        {!canCheck
          ? t("guest.uncertainNoCheck.body")
          : checkAlsoLost
            ? t("flow.review.uncertain.bodyAgain")
            : t("flow.review.uncertain.body")}
      </Line>
    </Screen>
  );
}

export function AlreadyBookedUnreachable({ business }: { business: string }) {
  const { t } = useTranslation();
  return (
    <Screen testId="guest-already-booked" heading={t("guest.alreadyBooked.heading")} outcome={{ business }} foot={<HelpLink />}>
      <Line tone="warning" role="alert">{t("guest.alreadyBooked.body")}</Line>
    </Screen>
  );
}

export function CannotStart({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <Screen
      testId="guest-cannot-start"
      heading={t("guest.cannotStart.heading")}
      foot={
        <button type="button" className="b-btn v-primary is-full" onClick={onRetry} data-testid="guest-cannot-start-retry">
          {t("flow.review.failed.action")}
        </button>
      }
    >
      <p className="b-p">{t("guest.cannotStart.body")}</p>
    </Screen>
  );
}
